import React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from "lucide-react";
import type { AdminProductItem, AdminSortField } from "../hooks/useAdminProductsList";
import type { ColumnFilters } from "../hooks/useAdminColumnFilters";
import type { ProductMetadata } from "../../../../types/productMetadata";
import { METADATA_FIELD_CONFIGS } from "../../../../types/productMetadata";
import type { Visibility } from "../../../../types/subscription";
import ScrollableTableWrapper from "../../../../components/ScrollableTableWrapper";
import AdminProductRow from "./AdminProductRow";
import AdminColumnFilterPopover from "./AdminColumnFilterPopover";

interface Column {
  key: string;
  label: string;
  tooltip?: string;
  sortKey?: AdminSortField;
  align: "left" | "right" | "center";
  responsiveClass?: string;
  widthClass?: string;
}

const BASE_COLUMNS: Column[] = [
  { key: "id", label: "ID", sortKey: "id", align: "left", responsiveClass: "hidden lg:table-cell" },
  { key: "type", label: "T", tooltip: "Type", sortKey: "productType", align: "center" },
  { key: "title", label: "Product", sortKey: "title", align: "left", widthClass: "max-w-[280px]" },
  { key: "created", label: "Created", sortKey: "created", align: "right", responsiveClass: "hidden md:table-cell" },
  { key: "provider", label: "Provider", sortKey: "provider", align: "left", responsiveClass: "hidden md:table-cell" },
  { key: "visibility", label: "Vis", tooltip: "Visibility", sortKey: "visibility", align: "left" },
];

const COMPOSITION_COLUMNS: Column[] = [
  { key: "longCount", label: "L", tooltip: "Longs", sortKey: "longCount", align: "right" },
  { key: "shortCount", label: "S", tooltip: "Shorts", sortKey: "shortCount", align: "right" },
  { key: "optionCount", label: "Opt", tooltip: "Options", sortKey: "optionCount", align: "right" },
  { key: "grossExposure", label: "Gross", tooltip: "Gross Exposure", sortKey: "grossExposure", align: "right" },
  { key: "netExposure", label: "Net", tooltip: "Net Exposure", sortKey: "netExposure", align: "right" },
];

const STATUS_COLUMN: Column = { key: "investible-status", label: "", tooltip: "Investible Status", align: "center" };

const TAG_COLUMNS: Column[] = METADATA_FIELD_CONFIGS.map((config) => ({
  key: config.key,
  label: config.label,
  tooltip: config.label,
  align: "center" as const,
}));

const DATE_COLUMNS: Column[] = [
  { key: "updated", label: "Updated", sortKey: "updated", align: "right" },
];

const REMARKS_COLUMN: Column = {
  key: "contractRemarks",
  label: "Remarks",
  tooltip: "Contract Remarks",
  align: "center",
};

const ACTIONS_COLUMN: Column = {
  key: "actions",
  label: "",
  tooltip: "Actions",
  align: "center",
};

const ALL_COLUMNS: Column[] = [
  ...BASE_COLUMNS,
  ...COMPOSITION_COLUMNS,
  STATUS_COLUMN,
  ...TAG_COLUMNS,
  ...DATE_COLUMNS,
  REMARKS_COLUMN,
  ACTIONS_COLUMN,
];

const EMPTY_SET = new Set<string>();

function SortIcon({ col, sortField, sortDesc }: { col: Column; sortField: AdminSortField; sortDesc: boolean }) {
  if (!col.sortKey) return null;
  if (col.sortKey !== sortField) {
    return <ArrowUpDown className="w-3 h-3 inline ml-0.5 text-rp-grey/30" />;
  }
  return sortDesc
    ? <ArrowDown className="w-3 h-3 inline ml-0.5 text-rp-amber" />
    : <ArrowUp className="w-3 h-3 inline ml-0.5 text-rp-amber" />;
}

interface AdminProductsTableProps {
  products: AdminProductItem[];
  sortField: AdminSortField;
  sortDesc: boolean;
  onSort: (field: AdminSortField) => void;
  columnFilters: ColumnFilters;
  filterOptions: Record<string, string[]>;
  onToggleFilter: (columnKey: string, value: string) => void;
  onSelectAllFilter: (columnKey: string, values: string[]) => void;
  onClearFilter: (columnKey: string) => void;
  getMetadata: (product: AdminProductItem) => ProductMetadata | null;
  isSaving: (product: AdminProductItem) => boolean;
  isVisibilitySaving: (product: AdminProductItem) => boolean;
  onProductClick: (product: AdminProductItem) => void;
  onAnalyzeClick: (product: AdminProductItem) => void;
  onRemarksClick: (product: AdminProductItem, anchor: DOMRect) => void;
  onTagChange: (
    product: AdminProductItem,
    key: keyof ProductMetadata,
    value: string | undefined
  ) => void;
  onVisibilityChange: (product: AdminProductItem, next: Visibility) => void;
}

const AdminProductsTable: React.FC<AdminProductsTableProps> = ({
  products,
  sortField,
  sortDesc,
  onSort,
  columnFilters,
  filterOptions,
  onToggleFilter,
  onSelectAllFilter,
  onClearFilter,
  getMetadata,
  isSaving,
  isVisibilitySaving,
  onProductClick,
  onAnalyzeClick,
  onRemarksClick,
  onTagChange,
  onVisibilityChange,
}) => {
  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Search className="w-12 h-12 mb-4 text-rp-grey" />
        <p className="text-sm text-rp-grey">No products match your filters.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden border border-rp-separator bg-rp-surface">
      <ScrollableTableWrapper showScrollbar>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-rp-surface-sunken text-rp-grey/70">
              {ALL_COLUMNS.map((col) => {
                const isSortable = !!col.sortKey;
                const isFilterable = !!filterOptions[col.key];
                const hasActiveFilter = (columnFilters[col.key]?.size ?? 0) > 0;
                return (
                  <th
                    key={col.key}
                    className={`px-2 py-2 font-bold uppercase text-[11px] tracking-wider border-b border-rp-separator whitespace-nowrap ${
                      col.align === "right"
                        ? "text-right"
                        : col.align === "center"
                        ? "text-center"
                        : ""
                    } ${col.responsiveClass ?? ""} ${col.widthClass ?? ""} ${
                      isSortable ? "cursor-pointer hover:text-rp-amber transition-colors select-none" : ""
                    } ${hasActiveFilter ? "bg-rp-amber/5" : ""}`}
                    title={col.tooltip}
                    onClick={col.sortKey ? () => onSort(col.sortKey!) : undefined}
                  >
                    <span>{col.label}</span>
                    <SortIcon col={col} sortField={sortField} sortDesc={sortDesc} />
                    {isFilterable && (
                      <AdminColumnFilterPopover
                        columnKey={col.key}
                        options={filterOptions[col.key]}
                        label={col.label || col.key}
                        selectedValues={columnFilters[col.key] ?? EMPTY_SET}
                        onToggleValue={onToggleFilter}
                        onSelectAll={onSelectAllFilter}
                        onClear={onClearFilter}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-rp-separator">
            {products.map((product) => (
              <AdminProductRow
                key={`${product.productType}-${product.providerId}-${product.id}`}
                product={product}
                metadata={getMetadata(product)}
                saving={isSaving(product)}
                visibilitySaving={isVisibilitySaving(product)}
                onClick={onProductClick}
                onAnalyzeClick={onAnalyzeClick}
                onRemarksClick={onRemarksClick}
                onTagChange={onTagChange}
                onVisibilityChange={onVisibilityChange}
              />
            ))}
          </tbody>
        </table>
      </ScrollableTableWrapper>
    </div>
  );
};

export default AdminProductsTable;
